import React, { useState } from 'react';
import { 
  Target, 
  Layers, 
  Trash2, 
  HardDrive, 
  Folder as FoldersIcon,
  ChevronDown,
  PieChart,
  Settings,
  Shield,
  Activity
} from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';

const Sidebar = ({ 
  deleteMode, 
  setDeleteMode, 
  selectedTarget, 
  handleTargetSelection,
  performanceData,
  deleteModesConfig,
  optDuration,
  deletionStats
}) => {
  const [expandedSections, setExpandedSections] = useState({
    deleteMode: true,
    targetType: true,
    performance: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  return (
    <>
      <div className="p-4 border-b border-gray-800">
        <h2 className="text-lg font-semibold text-white flex items-center">
          <Settings className="mr-2 text-blue-400" />
          Configuration
        </h2>
      </div>
      
      <nav className="p-4 space-y-6">
        {/* Delete Mode Section */}
        <div className="space-y-3">
          <div 
            className="flex items-center justify-between cursor-pointer" 
            onClick={() => toggleSection('deleteMode')}
          >
            <h3 className="text-md font-medium flex items-center">
              <Target className="mr-2 text-green-400" /> 
              <span>Deletion Mode</span>
            </h3>
            <ChevronDown 
              className={`text-gray-400 transition-transform ${expandedSections.deleteMode ? 'rotate-180' : ''}`} 
              size={18} 
            />
          </div>
          
          {expandedSections.deleteMode && (
            <div className="mt-2 space-y-2 pl-1">
              {Object.keys(deleteModesConfig).map((mode) => {
                const ModeIcon = deleteModesConfig[mode].icon;
                return (
                  <button
                    key={mode}
                    onClick={() => setDeleteMode(mode)}
                    className={`w-full relative p-3 rounded-md flex items-center transition-all ${
                      deleteMode === mode
                        ? `${deleteModesConfig[mode].color} shadow-md`
                        : 'bg-gray-800 hover:bg-gray-700'
                    }`}
                  >
                    <ModeIcon className={`mr-3 ${deleteMode === mode ? 'text-white' : 'text-gray-400'}`} />
                    <div>
                      <span className="font-medium">{deleteModesConfig[mode].label}</span>
                      {deleteMode === mode && (
                        <span className="block text-xs mt-1 opacity-80">{deleteModesConfig[mode].description || 'Active'}</span>
                      )}
                    </div>
                    {deleteMode === mode && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 h-2 w-2 rounded-full bg-white"></div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Target Type Section */}
        <div className="space-y-3">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('targetType')}
          >
            <h3 className="text-md font-medium flex items-center">
              <Layers className="mr-2 text-blue-400" /> 
              <span>Target Type</span>
            </h3>
            <ChevronDown 
              className={`text-gray-400 transition-transform ${expandedSections.targetType ? 'rotate-180' : ''}`} 
              size={18} 
            />
          </div>
          
          {expandedSections.targetType && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {[
                { icon: Trash2, label: "Files", color: "text-red-400", bgActive: "bg-gradient-to-br from-red-600 to-red-800" },
                { icon: HardDrive, label: "Disk", color: "text-green-400", bgActive: "bg-gradient-to-br from-green-600 to-green-800" },
                { icon: FoldersIcon, label: "Folders", color: "text-purple-400", bgActive: "bg-gradient-to-br from-purple-600 to-purple-800" },
              ].map(({ icon: Icon, label, color, bgActive }) => (
                <div
                  key={label}
                  className={`p-3 rounded-lg text-center cursor-pointer transition-all hover:shadow-md ${
                    selectedTarget === label 
                      ? `${bgActive} shadow-lg border border-gray-700` 
                      : "bg-gray-800 hover:bg-gray-700"
                  }`}
                  onClick={() => handleTargetSelection(label)}
                >
                  <Icon className={`mx-auto mb-2 ${selectedTarget === label ? 'text-white' : color}`} size={20} />
                  <span className={`text-xs font-medium ${selectedTarget === label ? 'text-white' : ''}`}>{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Performance Metrics Section */}
        <div className="space-y-3">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('performance')}
          >
            <h3 className="text-md font-medium flex items-center">
              <Activity className="mr-2 text-orange-400" /> 
              <span>Performance Metrics</span>
            </h3>
            <ChevronDown 
              className={`text-gray-400 transition-transform ${expandedSections.performance ? 'rotate-180' : ''}`} 
              size={18} 
            />
          </div>
          
          {expandedSections.performance && (
            <div className="mt-3 p-4 bg-gray-800 rounded-lg border border-gray-700 shadow-inner">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-400">Throughput</span>
                <span className="text-sm font-semibold text-white">{deletionStats.deletionRate}</span>
              </div>
              
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={performanceData} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
                  <defs>
                    <linearGradient id="speedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Tooltip 
                    contentStyle={{ 
                      background: 'rgba(17, 24, 39, 0.95)', 
                      border: '1px solid #374151',
                      borderRadius: '4px',
                      color: '#fff'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="speed" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    fill="url(#speedGradient)" 
                  />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                  <YAxis hide />
                </AreaChart>
              </ResponsiveContainer>
              
              <div className="grid grid-cols-2 gap-2 mt-4">
                <div className="p-2 bg-gray-900 rounded border border-gray-700 flex flex-col">
                  <span className="text-xs text-gray-400">Total Processed</span>
                  <span className="text-sm font-medium">{deletionStats.totalSize}</span>
                </div>
                <div className="p-2 bg-gray-900 rounded border border-gray-700 flex flex-col">
                  <span className="text-xs text-gray-400">Est. Completion</span>
                  <span className="text-sm font-medium">{optDuration}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Security Status Indicator */}
        <div className="p-4 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 rounded-lg border border-blue-800/50 mt-4">
          <div className="flex items-center space-x-3">
            <Shield className="text-blue-400" />
            <div>
              <h4 className="font-medium">Secure Mode Active</h4>
              <p className="text-xs text-gray-400 mt-1">Encrypted operations enabled</p>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Version & Status Footer */}
      <div className="absolute bottom-0 left-0 right-0 p-3 text-xs text-center text-gray-500 border-t border-gray-800 bg-gray-900/80">
        <div className="flex items-center justify-center">
          <span className="h-2 w-2 rounded-full bg-green-500 mr-2"></span>
          System Status: • v3.4.2
        </div>
      </div>
    </>
  );
};

export default Sidebar;